import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { getUserRole, canAccessAdmin } from '../utils/roles';
import { Lock, Eye, EyeOff, Loader2, ShieldAlert, Clock } from 'lucide-react';
import { Btn } from '../design/ui';

const AdminPinContext = createContext({ isPinVerified: false, lockSession: () => {} });

export const useAdminPin = () => useContext(AdminPinContext);

// NOTA KESELAMATAN: PIN tidak lagi disimpan dalam kod ini. Ia disemak di server
// (Cloud Function verifyAdminPin) dan disimpan hashed di Firestore admin_security/__pin__.
const SESSION_KEY = 'admin_pin_verified';
const SESSION_TIME_KEY = 'admin_pin_time';
const SESSION_UID_KEY = 'admin_pin_uid'; // ikat sesi PIN pada akaun — elak login lain warisi status
const SESSION_DURATION = 30 * 60 * 1000; // 30 min
const IDLE_CLEAR_MS = 30 * 1000; // auto-clear kotak PIN selepas 30s tak sentuh
const EMPTY_PIN = ['', '', '', '', '', ''];

// Baca kod error callable dengan/ tanpa prefix "functions/"
const errCode = (e) => (e?.code || '').replace(/^functions\//, '');

// allowNonPrivileged: bila true, user bukan-admin (customer biasa) TERUS lalu tanpa PIN
// — dipakai untuk gate /workspace (customer tak boleh dikunci dari page utama mereka;
// hanya akaun admin/moderator kena PIN, kongsi sesi yang sama dgn panel admin).
const AdminPinGate = ({ children, allowNonPrivileged = false, onVerifiedChange }) => {
  const [isPinVerified, setIsPinVerified] = useState(false);
  const [pinInput, setPinInput] = useState([...EMPTY_PIN]);
  const [pinError, setPinError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  const [showPin, setShowPin] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [verifying, setVerifying] = useState(false);

  // Status PIN dari server: null = tengah semak, true = dah di-set, false = belum
  const [pinConfigured, setPinConfigured] = useState(null);
  const [canSetup, setCanSetup] = useState(false);
  const [statusError, setStatusError] = useState('');

  // Skrin set PIN kali pertama (master admin): 'enter' (cipta) → 'confirm' (ulang)
  const [setupStage, setSetupStage] = useState('enter');
  const setupFirstRef = useRef('');

  const pinRefs = useRef([]);
  const sessionTimeout = useRef(null);
  const idleTimer = useRef(null);

  const resetIdleTimer = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      setPinInput([...EMPTY_PIN]);
    }, IDLE_CLEAR_MS);
  };

  // Ambil status PIN dari server (adakah dah di-set + bolehkah caller set)
  const loadStatus = () => {
    setStatusError('');
    setPinConfigured(null);
    httpsCallable(functions, 'getAdminPinStatus')()
      .then((res) => {
        setPinConfigured(!!res.data?.configured);
        setCanSetup(!!res.data?.canSetup);
        setSetupStage('enter');
        setupFirstRef.current = '';
      })
      .catch((e) => {
        // Gagal sambung → jangan dedah skrin setup; tunjuk ralat + butang cuba semula
        setPinConfigured(true);
        setCanSetup(false);
        setStatusError(errCode(e) === 'permission-denied'
          ? 'This account has no admin access.'
          : 'Could not reach the server. Try again.');
      });
  };

  // Check role & session on mount
  useEffect(() => {
    setPinInput([...EMPTY_PIN]);

    const email = auth.currentUser?.email;
    const role = getUserRole(email);
    const authorized = canAccessAdmin(role);
    setIsAuthorized(authorized);

    let verified = false;
    const session = sessionStorage.getItem(SESSION_KEY);
    const sessionTime = sessionStorage.getItem(SESSION_TIME_KEY);
    if (session === 'true' && sessionTime) {
      const elapsed = Date.now() - parseInt(sessionTime);
      if (elapsed < SESSION_DURATION) {
        verified = true;
        setIsPinVerified(true);
      } else {
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_TIME_KEY);
      }
    }
    setChecking(false);

    // Perlu semak status PIN hanya kalau layak & belum verified
    if (authorized && !verified) loadStatus();

    return () => { if (idleTimer.current) clearTimeout(idleTimer.current); };
  }, []);

  // Lock timer countdown
  useEffect(() => {
    if (lockTimer > 0) {
      const t = setTimeout(() => setLockTimer(lockTimer - 1), 1000);
      return () => clearTimeout(t);
    } else if (isLocked && lockTimer === 0) {
      setIsLocked(false);
    }
  }, [lockTimer, isLocked]);

  // Session auto-expire
  useEffect(() => {
    if (isPinVerified) {
      sessionTimeout.current = setTimeout(() => {
        setIsPinVerified(false);
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_TIME_KEY);
      }, SESSION_DURATION);
      return () => clearTimeout(sessionTimeout.current);
    }
  }, [isPinVerified]);

  // Lapor status verified ke parent (App.jsx guna untuk kunci chrome workspace:
  // panel kiri + toolbar terapung yang di-render di luar gate ini).
  useEffect(() => {
    if (checking) return;
    if (typeof onVerifiedChange === 'function') onVerifiedChange(isPinVerified);
  }, [isPinVerified, checking]);

  const lockSession = () => {
    setIsPinVerified(false);
    setPinInput([...EMPTY_PIN]);
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_TIME_KEY);
    // Semak semula status supaya balik ke skrin masuk PIN
    if (isAuthorized) loadStatus();
  };

  const markVerified = () => {
    setIsPinVerified(true);
    setPinError('');
    setPinInput([...EMPTY_PIN]);
    sessionStorage.setItem(SESSION_KEY, 'true');
    sessionStorage.setItem(SESSION_TIME_KEY, Date.now().toString());
    sessionStorage.setItem(SESSION_UID_KEY, auth.currentUser?.uid || '');
  };

  const clearBoxes = () => {
    setPinInput([...EMPTY_PIN]);
    setTimeout(() => pinRefs.current[0]?.focus(), 0);
  };

  // ── Semak PIN (skrin entry) ──
  const verifyPin = async (pin) => {
    if (verifying) return;
    setVerifying(true);
    try {
      await httpsCallable(functions, 'verifyAdminPin')({ pin });
      markVerified();
    } catch (e) {
      const code = errCode(e);
      const details = e?.details || {};
      clearBoxes();
      if (code === 'resource-exhausted') {
        const secs = Number(details.lockedForSeconds) || 300;
        setIsLocked(true);
        setLockTimer(secs);
        setPinError(`Too many attempts. Try again in ${secs} seconds.`);
      } else if (code === 'permission-denied' && typeof details.attemptsLeft === 'number') {
        setPinError(`Wrong PIN. ${details.attemptsLeft} tries left.`);
      } else if (code === 'failed-precondition') {
        // PIN belum di-set — kembali ke pemeriksaan status
        setPinError('Admin PIN is not set yet.');
        loadStatus();
      } else {
        setPinError(e?.message || 'Could not check your PIN. Try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  // ── Set PIN kali pertama (skrin setup, master admin) ──
  const handleSetupComplete = async (pin) => {
    if (verifying) return;
    if (setupStage === 'enter') {
      setupFirstRef.current = pin;
      setSetupStage('confirm');
      setPinError('');
      clearBoxes();
      return;
    }
    // stage === 'confirm'
    if (pin !== setupFirstRef.current) {
      setPinError("PINs don't match. Create your PIN again.");
      setupFirstRef.current = '';
      setSetupStage('enter');
      clearBoxes();
      return;
    }
    setVerifying(true);
    try {
      await httpsCallable(functions, 'setAdminPin')({ pin });
      markVerified();
    } catch (e) {
      const code = errCode(e);
      setupFirstRef.current = '';
      setSetupStage('enter');
      clearBoxes();
      if (code === 'failed-precondition') {
        // Dah di-set (race) → tukar ke skrin masuk PIN
        setPinConfigured(true);
        setPinError('The PIN is already set. Enter your PIN.');
      } else {
        setPinError(e?.message || 'Could not save the PIN. Try again.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const onComplete = (pin) => {
    if (pinConfigured === false && canSetup) handleSetupComplete(pin);
    else verifyPin(pin);
  };

  const handlePinChange = (index, value) => {
    if (isLocked || verifying) return;
    const v = value.replace(/\D/g, '').slice(-1);
    const newPin = [...pinInput];
    newPin[index] = v;
    setPinInput(newPin);
    setPinError('');
    resetIdleTimer();

    if (v && index < 5) {
      pinRefs.current[index + 1]?.focus();
    }

    if (newPin.every(d => d !== '') && newPin.join('').length === 6) {
      setTimeout(() => onComplete(newPin.join('')), 100);
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinInput[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    if (isLocked || verifying) return;
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setPinInput(pasted.split(''));
      setTimeout(() => onComplete(pasted), 100);
    }
  };

  if (checking) return null;

  // Customer biasa di page yang benarkan bukan-admin (cth /workspace) — terus lalu, tiada PIN
  if (!isAuthorized && allowNonPrivileged) {
    return <>{children}</>;
  }

  // Not authorized
  if (!isAuthorized) {
    return (
      <BingkaiPin>
        <ShieldAlert size={22} style={IKON_ATAS} aria-hidden="true" />
        <h1 className="s-title" style={{ fontSize: 20, margin: '10px 0 4px' }}>Access denied</h1>
        <p className="s-subtitle">This page is for admins and moderators only.</p>
      </BingkaiPin>
    );
  }

  // Verified — render children with context
  if (isPinVerified) {
    return (
      <AdminPinContext.Provider value={{ isPinVerified, lockSession }}>
        {children}
      </AdminPinContext.Provider>
    );
  }

  // Nota: TAK tunggu getAdminPinStatus sebelum papar kotak PIN.
  // Selepas admin lama tak aktif, fungsi server "tidur" (cold start) → dulu skrin
  // tersekat "Memuatkan..." beberapa saat. Sekarang kotak PIN (entry) terus muncul;
  // semakan status jalan di belakang. Kalau ternyata first-run (belum set PIN),
  // ia auto-tukar ke skrin "Set PIN Admin" bila status tiba (kes jarang, sekali je).

  // Moderator tapi PIN belum di-set oleh admin utama (hanya selepas status disahkan)
  if (pinConfigured === false && !canSetup) {
    return (
      <BingkaiPin>
        <Clock size={22} style={IKON_ATAS} aria-hidden="true" />
        <h1 className="s-title" style={{ fontSize: 20, margin: '10px 0 4px' }}>Admin PIN not set yet</h1>
        <p className="s-subtitle">The main admin has not set the admin panel PIN. Ask the main admin to set it first.</p>
      </BingkaiPin>
    );
  }

  const isSetup = pinConfigured === false && canSetup;
  const subtitle = isSetup
    ? (setupStage === 'enter'
        ? 'Create a new 6-digit admin PIN'
        : 'Type the same PIN again')
    : 'Enter your 6-digit admin PIN';

  // PIN gate (setup atau entry — kongsi UI kotak PIN). Rupa = pintu Partner Studio
  // (PartnerPinPintu.jsx): kad v3.9 monokrom, token --s-* (ikut tema gelap/cerah).
  return (
    <BingkaiPin>
      <Lock size={22} style={IKON_ATAS} aria-hidden="true" />
      <h1 className="s-title" style={{ fontSize: 20, margin: '10px 0 4px' }}>
        {isSetup ? 'Set admin PIN' : 'Admin verification'}
      </h1>
      <p className="s-subtitle" style={{ marginBottom: 18 }}>{subtitle}</p>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }} onPaste={handlePinPaste}>
        {pinInput.map((digit, i) => (
          <input
            key={i}
            ref={el => pinRefs.current[i] = el}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            name={`av-${i}`}
            maxLength={1}
            value={digit}
            onChange={e => handlePinChange(i, e.target.value)}
            onKeyDown={e => handlePinKeyDown(i, e)}
            disabled={isLocked || verifying}
            aria-label={`PIN digit ${i + 1}`}
            className="s-input"
            style={{
              width: 42, height: 48, padding: 0, textAlign: 'center', fontSize: 18,
              WebkitTextSecurity: showPin ? 'none' : 'disc',
              ...(pinError && !isLocked ? { borderColor: 'var(--s-stop)' } : {}),
            }}
            autoFocus={i === 0}
          />
        ))}
      </div>

      {verifying && (
        <p className="s-hint" style={{ marginTop: 12 }}>
          <Loader2 size={13} className="animate-spin" style={{ display: 'inline-block', verticalAlign: -2 }} /> {isSetup ? 'Saving…' : 'Checking…'}
        </p>
      )}
      {isLocked && lockTimer > 0 && (
        <p className="s-hint" style={{ marginTop: 12, color: 'var(--s-stop)' }}>
          Locked. Try again in {Math.floor(lockTimer / 60)}:{String(lockTimer % 60).padStart(2, '0')}.
        </p>
      )}
      {pinError && !isLocked && (
        <p className="s-hint" style={{ marginTop: 12, color: 'var(--s-stop)' }}>{pinError}</p>
      )}
      {statusError && !pinError && (
        <div style={{ marginTop: 12 }}>
          <p className="s-hint" style={{ margin: 0 }}>{statusError}</p>
          <Btn variant="quiet" size="sm" onClick={loadStatus} style={{ marginTop: 6 }}>Try again</Btn>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <Btn
          variant="quiet"
          size="sm"
          onClick={() => setShowPin(!showPin)}
          icon={showPin ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
        >
          {showPin ? 'Hide PIN' : 'Show PIN'}
        </Btn>
      </div>

      <p className="s-hint" style={{ marginTop: 14 }}>
        {isSetup ? 'This PIN is stored securely on the server.' : 'Your session ends after 30 minutes of inactivity.'}
      </p>
    </BingkaiPin>
  );
};

// Ikon atas kad. `display:block` + margin auto WAJIB: preflight Tailwind menjadikan <svg>
// blok, jadi `textAlign:center` kad tidak menengahkannya (ikon lari ke kiri dalam app).
const IKON_ATAS = { display: 'block', margin: '0 auto', color: 'var(--s-ink-3)' };

// Bingkai semua skrin gate. Latar LUTSINAR (mengatasi `.s-root { background: --s-ground }`):
// ikut panel halaman. Dahulu --s-ground setinggi 70vh = belang kelabu/putih di bawah kad. `s-root` membawa muka taip + saiz teks v3.9 (gate dirender
// di dalam halaman yang tidak semestinya berskop `s-root`). Token warna --s-* datang dari
// `body.s-v39` (rupa v3.9 — config/ui_v39 hidup untuk semua sejak 22 Ogos 2026).
function BingkaiPin({ children }) {
  return (
    <div
      className="s-root"
      style={{ flex: 1, width: '100%', minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'transparent' }}
    >
      <div className="s-card" style={{ width: '100%', maxWidth: 380, padding: '28px 24px', textAlign: 'center' }}>
        {children}
      </div>
    </div>
  );
}

export default AdminPinGate;
